import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { NavigationItem } from "./types";
import { splitChildrenByVisibility } from "@/lib/hooks/settings/nav-customization/selectors";
import { DesktopDropdownChild } from "./dropdown/DesktopDropdownChild";
import { DesktopDropdownMore } from "./dropdown/DesktopDropdownMore";
import { NavItemLink } from "./nav-item-link";

interface NavigationRendererProps {
  items: NavigationItem[];
  pathname: string;
  isMobile?: boolean;
  onItemClick?: () => void;
}

export function NavigationRenderer({
  items,
  pathname,
  isMobile = false,
  onItemClick,
}: NavigationRendererProps) {
  if (isMobile) {
    return (
      <div className="py-2">
        {items.map((item) => {
          const Icon = item.icon;
          const isCurrent = pathname.startsWith(item.href);
          return (
            <div key={item.name}>
              <NavItemLink
                item={item}
                aria-current={isCurrent ? "page" : undefined}
                className={cn(
                  "flex items-center px-4 py-2 text-sm font-medium",
                  isCurrent
                    ? "bg-accent text-foreground"
                    : "text-foreground hover:text-foreground hover:bg-accent"
                )}
                onClick={onItemClick}
              >
                {Icon && <Icon className="mr-3 h-4 w-4" />}
                {item.name}
              </NavItemLink>

              {item.children && item.children.length > 0 && (
                <div className="ml-4 border-l border-border">
                  {item.children.map((child) => {
                    const hasGrandchildren = child.children && child.children.length > 0;
                    return (
                    <div key={child.name}>
                      <NavItemLink
                        item={child}
                        className="flex items-center justify-between px-4 py-2 text-sm text-foreground hover:text-foreground hover:bg-accent"
                        onClick={onItemClick}
                      >
                        <span>{child.name}</span>
                        {hasGrandchildren && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </NavItemLink>

                      {/* Nested children for mobile */}
                      {hasGrandchildren && child.children && (
                        <div className="ml-4 border-l border-border">
                          {child.children.map((grandchild) => (
                            <NavItemLink
                              key={grandchild.name}
                              item={grandchild}
                              className="block px-4 py-2 text-sm text-foreground hover:text-foreground hover:bg-accent"
                              onClick={onItemClick}
                            >
                              {grandchild.name}
                            </NavItemLink>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      {items.map((item) => {
        const Icon = item.icon;
        const isCurrent = pathname.startsWith(item.href);
        const hasChildren = item.children && item.children.length > 0;

        return (
          <div 
            key={item.name} 
            className="relative group focus-within:z-50"
            tabIndex={hasChildren ? 0 : undefined}
          >
            <NavItemLink
              item={item}
              aria-current={isCurrent ? "page" : undefined}
              className={cn(
                "flex items-center px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors",
                // iPad-friendly touch targets - larger padding on tablet sizes
                "md:px-3 md:py-2 lg:px-2.5 lg:py-1.5",
                // Better touch targets for iPad
                "min-h-[44px] md:min-h-[44px] lg:min-h-[32px]",
                isCurrent
                  ? "bg-accent text-foreground"
                  : "text-foreground hover:text-foreground hover:bg-accent"
              )}
            >
              {Icon && <Icon className="mr-2 h-4 w-4 md:h-5 md:w-5 lg:h-4 lg:w-4" />}
              {item.name}
            </NavItemLink>

            {/* Dropdown for items with children */}
            {hasChildren && (
              <>
                {/* Invisible hover bridge to maintain hover state */}
                <div className="absolute top-full left-0 w-full h-4 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"></div>

                <div className="absolute top-full left-0 mt-2 w-64 bg-popover border border-border rounded-lg shadow-xl invisible opacity-0 scale-95 group-hover:visible group-hover:opacity-100 group-hover:scale-100 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:scale-100 transition-all duration-200 z-50 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto">
                  {(() => {
                    // Split submenu entries into the up-front list and the
                    // collapsed "More" group based on per-child visibility.
                    const { visible: visibleChildren, hidden: hiddenChildren } =
                      splitChildrenByVisibility(item.children);
                    return (
                      <div className="py-2">
                        {visibleChildren.map((child) => (
                          <DesktopDropdownChild key={child.name} child={child} />
                        ))}
                        <DesktopDropdownMore items={hiddenChildren} />
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
